
import SpeakerCard from "./component/speaker-card";
import BulletPoint from "./component/bullet-point";
import AgendaItem from "./component/agenda-item";
import toast from "react-hot-toast";
import { useState } from "react";
import { Helmet, HelmetData } from "react-helmet-async";
import PictureCarousel from "./component/picture-carousel";
import { whyAttendItems, whoShouldJoinItems, speakers } from "./constants";
import { EMAIL_SERVICE, INTERNAL_RECIPIENTS } from "../../../utils/constants";
import { sendFormSubmissionDetails } from "../../../utils/templates";
const helmetData = new HelmetData({});

export default function AIEvent() {
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    const form = new FormData(e.target);
    const payload = {
      ...Object.fromEntries(form),
    };

    try {
      //  Send submission to the team
      INTERNAL_RECIPIENTS.forEach((user) => {
        const internalData = new FormData();
        internalData.append("to", user);
        internalData.append("subject", "Post-Event Meeting AI Global");
        internalData.append(
          "body",
          sendFormSubmissionDetails(
            payload,
            "Request a Post-Event Meeting Form AI Global"
          )
        );
        internalData.append("messageType", "html");
        internalData.append("from", "no-reply@dataassistant.ai");
        internalData.append("senderName", "AI Global website");

        fetch(EMAIL_SERVICE, {
          method: "POST",
          body: internalData,
        });
      });

      handleSuccess(
        "🎉 Your submission was successful! We'll contact you shortly."
      );
      e.target.reset();
    } catch (error) {
      toast.error("An error occurred. Please try again later.");
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSuccess = (message) => {
    toast.dismiss();
    toast.success(message, {
      duration: 3000,
      position: "top-center",
      style: {
        background: "#2F4171",
        color: "#fff",
        fontWeight: "bold",
      },
    });
  };

  return (
    <>
      <Helmet helmetData={helmetData}>
        <title>
          [AI] Data Stack Connect 2025 | AI Agents &amp; the Data Stack
        </title>
        <link
          rel="canonical"
          href="https://analyticsintelligence.com/event/ai-data-stack-connect-2025"
        />
        <meta
          name="description"
          content="[AI] Data Stack Connect 2025 — 'From Data to Insight with AI Agents.' A London event by Analytics Intelligence bringing together data and AI leaders to explore AI-driven analytics and the modern data stack."
        />
        <meta
          name="keywords"
          content="[AI] Data Stack Connect 2025, AI agents, modern data stack, AI-driven analytics event, Analytics Intelligence event, London data conference, large language models, data-driven decision-making"
        />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Analytics Intelligence Ltd" />
        <meta
          property="og:title"
          content="[AI] Data Stack Connect 2025 | AI Agents & the Data Stack"
        />
        <meta
          property="og:description"
          content="'From Data to Insight with AI Agents' — a London event by Analytics Intelligence bringing together data and AI leaders to explore AI-driven analytics and the modern data stack."
        />
        <meta
          property="og:url"
          content="https://analyticsintelligence.com/event/ai-data-stack-connect-2025"
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:image"
          content="https://analyticsintelligence.com/favicon.png"
        />
        <meta property="og:site_name" content="Analytics Intelligence" />
        <meta property="og:locale" content="en_GB" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:site" content="@_AnalyticsIntel" />
        <meta
          name="twitter:title"
          content="[AI] Data Stack Connect 2025 | AI Agents & the Data Stack"
        />
        <meta
          name="twitter:description"
          content="'From Data to Insight with AI Agents' — a London event by Analytics Intelligence exploring AI-driven analytics and the modern data stack."
        />
        <meta
          name="twitter:image"
          content="https://analyticsintelligence.com/favicon.png"
        />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "Event",
            "name": "[AI] Data Stack Connect 2025",
            "description": "From Data to Insight with AI Agents — an event by Analytics Intelligence for data experts and AI innovators exploring AI-driven analytics and the modern data stack.",
            "startDate": "2025-04-16T15:30:00+01:00",
            "endDate": "2025-04-16T19:00:00+01:00",
            "eventStatus": "https://schema.org/EventScheduled",
            "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
            "location": {
              "@type": "Place",
              "name": "One Canada Square, Canary Wharf",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "One Canada Square",
                "addressLocality": "London",
                "postalCode": "E14 5AB",
                "addressCountry": "GB"
              }
            },
            "organizer": {
              "@type": "Organization",
              "name": "Analytics Intelligence Ltd",
              "url": "https://analyticsintelligence.com"
            },
            "url": "https://analyticsintelligence.com/event/ai-data-stack-connect-2025"
          }
        `}</script>
      </Helmet>
      <main className="min-h-screen bg-[#fff]">
        <div className="text-white relative md:px-[86px] px-[10px] bg-ai_event_hero_bg bg-cover">
          {/* Main content */}
          <div className="container mx-auto px-4 min-h-screen flex items-center py-[96px] relative z-1">
            <div className="w-full flex flex-col md:flex-row items-center justify-between gap-8">
              {/* Left column - Event info */}
              <div>
                <h1 className="header-main font-sora text-[35px] md:text-[48px] lg:text-[51px] font-bold leading-[120%]">
                  [AI] Data Stack Connect 2025
                  <br />{" "}
                  <span className="header-main font-sora text-[35px] md:text-[48px] lg:text-[51px] font-bold leading-[120%]">
                    From Data to Insight with
                  </span>
                  <br />
                  <span className="header-main font-sora text-[35px] md:text-[48px] lg:text-[51px] font-bold leading-[120%]">
                    AI Agents
                  </span>
                </h1>

                <div className="flex items-center mt-8 mb-8">
                  <span className="text-[20px] font-[600]">April 16, 2025</span>
                </div>
                <p className="leading-[170%] max-w-[694px]">
                  <strong>[AI] Data Stack Connect</strong> is the premier event
                  by <strong>Analytics Intelligence</strong>, bringing together
                  industry leaders, data experts, and AI innovators to explore
                  the future of AI-driven analytics. Discover how enterprises
                  are building and deploying AI-ready applications, leveraging
                  the power of the modern data stack to drive insights and
                  automation.
                </p>
              </div>

              {/* Right column - Registration form */}
              <div className="ai-event-form p-6 md:p-8 mt-8 md:mt-0 w-full flex-shrink-0 lg:max-w-[486px]">
                <h2 className="font-sora text-2xl font-bold mb-4 md:mb-8">
                  Request a Post-Event Meeting
                </h2>
                <form
                  onSubmit={handleSubmit}
                  className="space-y-4 md:space-y-6"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <input
                      type="text"
                      name="firstName"
                      placeholder="First Name"
                      required
                      className="w-full px-4 py-2 rounded bg-white text-black border border-blue-400/30 focus:outline-none focus:ring-2 focus:ring-blue-400"
                    />
                    <input
                      type="text"
                      name="lastName"
                      placeholder="Last Name"
                      required
                      className="w-full px-4 py-2 rounded-[8px] bg-white text-black border border-blue-400/30 focus:outline-none focus:ring-2 focus:ring-blue-400"
                    />
                  </div>
                  <input
                    type="text"
                    name="company"
                    placeholder="Company"
                    required
                    className="w-full px-4 py-2 rounded-[8px] bg-white text-black border border-blue-400/30 focus:outline-none focus:ring-2 focus:ring-blue-400"
                  />
                  <input
                    type="text"
                    name="title"
                    placeholder="Title"
                    required
                    className="w-full px-4 py-2 rounded-[8px] bg-white text-black border border-blue-400/30 focus:outline-none focus:ring-2 focus:ring-blue-400"
                  />
                  <input
                    type="email"
                    name="email"
                    placeholder="Work Email"
                    required
                    className="w-full px-4 py-2 rounded-[8px] bg-white text-black border border-blue-400/30 focus:outline-none focus:ring-2 focus:ring-blue-400"
                  />
                  <input
                    type="tel"
                    name="phone"
                    placeholder="Phone Number (Optional)"
                    className="w-full px-4 py-2 rounded-[8px] bg-white text-black border border-blue-400/30 focus:outline-none focus:ring-2 focus:ring-blue-400"
                  />
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full py-3 btn-sweep btn-sweep-blue rounded-[100px] font-medium"
                  >
                    {submitting ? "Submitting..." : "Submit"}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-blue-500 h-4"></div>

        <PictureCarousel />

        <div className="sm:px-[80px] px-[12px] md:my-20 my-10 space-y-8 pb-[34px]">
          <h2 className="font-sora text-3xl font-bold  text-center">
            Thank you to all our speakers
          </h2>
          <div className="flex flex-wrap justify-center gap-8 md:gap-[74px] w-full">
            {speakers.map((speaker, index) => (
              <SpeakerCard
                key={index}
                name={speaker.name}
                role={speaker.role}
                company={speaker.company}
                imageUrl={speaker.imageUrl}
              />
            ))}
          </div>

          <div className="ai-event-agenda flex flex-col gap-[50px] sm:p-[24px] p-[12px] !mt-20">
            <div className="">
              <h2 className="font-sora text-3xl font-bold mb-8 text-[#2F4171]">
                Who Attended?
              </h2>
              <div className="space-y-4">
                {whoShouldJoinItems.map((item, index) => (
                  <BulletPoint
                    key={index}
                    title={item.title}
                    description={item.description}
                  />
                ))}
              </div>
            </div>

            <div className="">
              <h2 className="font-sora text-3xl  font-bold mb-8 text-[#2F4171]">
                Why They Attended?
              </h2>
              <div className="space-y-4">
                {whyAttendItems.map((item, index) => (
                  <BulletPoint
                    key={index}
                    title={item.title}
                    description={item.description}
                  />
                ))}
              </div>
            </div>

            {/* Agenda Section */}
            <div className="">
              <h2 className="font-sora text-3xl font-bold mb-8 text-[#2F4171]">
                Agenda
              </h2>

              <div className="bg-white text-black overflow-hidden py-6 md:py-8">
                <AgendaItem
                  time="15:30 - 16:00"
                  title="Registration and Welcome"
                  items={[
                    "Guest arrivals and registration.",
                    "Light refreshments available.",
                  ]}
                />

                <AgendaItem
                  time="16:00 - 16:30:"
                  title="Keynote Speeches"
                  items={[
                    "Representative from [AI] to introduce the mission and vision behind the Data Assistant.",
                    "Speaker from ObservePoint to highlight the collaboration and benefits of their tools.",
                    "Speaker from Tealium to highlight the collaboration and benefits of their tools.",
                  ]}
                />

                <AgendaItem
                  time="16:30 - 17:30:"
                  title="Panel Discussion"
                  customContent={
                    <div>
                      <div className="mb-2 ml-4">
                        <div>
                          <span className="font-bold">Topic:</span> Real World
                          Applications of Data Analytics and GenAI Tools
                        </div>
                        <div>
                          <span className="font-bold">Moderator:</span> David
                          Edoja - [AI] Analytics Intelligence
                        </div>
                      </div>

                      <div className="mb-2 ml-4">
                        <div>
                          <span className="font-bold">Panelists:</span>
                        </div>
                        <div className="ml-4">
                          <div>
                            Johnathan Conlon - Representative from Royal Mail to
                            share a use case for data automation and insights.
                          </div>
                          <div>
                            Charles Kellaway - Representative from LSE to talk
                            about the transformative impact of [AI]'s Data
                            Language Model.
                          </div>
                        </div>
                      </div>
                    </div>
                  }
                />

                <AgendaItem
                  time="17:30 - 19:00:"
                  title="Networking and Refreshments"
                />
              </div>
            </div>
          </div>
          <div className="mt-[20px] md:mt-[40px]">
            <h2 className="font-sora text-3xl lg:text-[36px] font-semibold text-[#2F4171] mb-8 text-center">
              Partners
            </h2>
            <div className="flex flex-wrap items-center justify-center gap-12 mb-12">
              <div className="w-48">
                <img
                  src="https://res.cloudinary.com/analytics-intelligence/image/upload/v1742814043/Tealium-logo-transparent_1_izturi.png"
                  alt="Tealium"
                  className="object-contain"
                />
              </div>
              <div className="w-48">
                <img
                  src="https://res.cloudinary.com/analytics-intelligence/image/upload/v1742814043/ObservePoint-Gray-Logo-1_1_hntud4.png"
                  alt="ObservePoint"
                  className="object-contain"
                />
              </div>
            </div>

            {/* Venue Section */}
            <h2 className="font-sora text-3xl lg:text-[36px] font-semibold text-[#2F4171] mb-6 text-center">
              Venue
            </h2>
            <div className="flex justify-center items-center mb-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 mr-2 text-[#1C1C1C]"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
              <span className="text-lg font-medium">
                One Canada Square, London E14 5AB
              </span>
            </div>
            <p className="text-center text-gray-600 mb-8 max-w-2xl mx-auto">
              One Canada Square is a landmark hub for progressive global
              businesses. Situated at the heart of Canary Wharf everything you
              need is on your doorstep.
            </p>

            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              <div className="">
                <img
                  src="https://res.cloudinary.com/analytics-intelligence/image/upload/v1742814044/c243dcd2-c92e-4201-8e8e-1b2cb3758a49-0_1_roelt0.png"
                  alt="One Canada Square Building"
                  fill
                  className="object-cover"
                />
              </div>
              <a
                href="https://maps.app.goo.gl/KaK63G9DaKa3urgj9"
                target="_blank"
                className=""
                rel="noreferrer"
              >
                <img
                  src="https://res.cloudinary.com/analytics-intelligence/image/upload/v1742814044/Screenshot_2025-03-24_at_10.24.37_2_kuyvhk.png"
                  alt="Map of One Canada Square"
                  fill
                  className="object-cover"
                />
              </a>
            </div>
          </div>
        </div>
      </main>
    </>
  );
}
